/**
 * Która ścieżka napisów włącza się sama.
 *
 * Reguła zmieniona 12.08.2026 na wniosek z oglądania: **materiał z polskich źródeł
 * bywa z napisami wtopionymi w obraz**. Nałożenie na to drugiej warstwy daje dwa
 * teksty jeden nad drugim — i to nie jest usterka renderowania, a błąd decyzji
 * o tym, co włączyć bez pytania.
 *
 * Rozstrzyga `origin`, bo tylko on mówi, SKĄD ścieżka się wzięła:
 *
 * - `source` — plik obok materiału, dostarczony przez to samo polskie źródło
 *   (paczka MEGA fansuba). Dokładnie ten przypadek: wydanie z wtopionymi napisami
 *   i osobny `.ass` w paczce. **Nie włączamy z automatu.**
 * - `embedded` — ścieżka wyjęta z samego pliku. Grupa, która wkłada napisy jako
 *   ścieżkę, nie wtapia ich jednocześnie w obraz, więc tutaj automat jest właściwy.
 * - `upload` i `animesub` — świadomy wybór widza: wgrał plik albo sam sięgnął po
 *   napisy. Wyłączanie tego byłoby ignorowaniem jego decyzji.
 *
 * Ścieżka wybrana ręcznie działa zawsze — zmienia się tylko to, co dzieje się bez
 * kliknięcia.
 */

/**
 * Ścieżka w kształcie, w jakim przychodzi z API.
 *
 * Świadomie własny, minimalny typ, a nie `SubtitleTrack` z `@anisync/types`: trasa
 * `/subtitles` oddaje tylko te pola, a reguła nie może udawać, że zna więcej.
 * Pierwsza wersja tej funkcji rozstrzygała po fladze `default` z pliku MKV — pola,
 * którego serwer w ogóle nie wysyła, czyli po warunku niemożliwym do spełnienia.
 */
export interface TrackForDefault {
  id: string;
  lang: string;
  origin: string;
}

/** Pochodzenia, którym wolno włączyć się same. */
const AUTO_ORIGINS: ReadonlySet<string> = new Set(['embedded', 'upload', 'animesub']);

export interface DefaultTrackChoice {
  /** Ścieżka do włączenia albo `null`, gdy zostajemy przy obrazie bez napisów. */
  trackId: string | null;
  /**
   * Czy powstrzymaliśmy się świadomie, mając polską ścieżkę pod ręką.
   *
   * Interfejs to pokazuje jednym zdaniem. Bez tego widz widzi wyłączone napisy przy
   * dostępnej polskiej ścieżce i nie ma jak zgadnąć, że to decyzja, a nie awaria —
   * czyli powtórzyłby się wzorzec, który ten projekt tępi od początku.
   */
  suppressedHardsub: boolean;
}

export function pickDefaultTrack(tracks: TrackForDefault[]): DefaultTrackChoice {
  const polish = tracks.filter((track) => track.lang === 'pl');
  if (polish.length === 0) return { trackId: null, suppressedHardsub: false };

  // Pierwsza polska ścieżka o zaufanym pochodzeniu. Kolejność jest ta z serwera,
  // czyli kolejność powstawania — ścieżka z pliku poprzedza dograne później.
  const chosen = polish.find((track) => AUTO_ORIGINS.has(track.origin)) ?? null;

  return {
    trackId: chosen?.id ?? null,
    // Powstrzymanie liczy się tylko wtedy, gdy REALNIE coś było do włączenia.
    suppressedHardsub: chosen === null,
  };
}
